import React, { useMemo } from "react";
import { AdminMessage } from "../../shared/types/adminMessage.type";
import { useLocales } from "../../hooks/useLocales";
import styled from "styled-components";
import { IconButton, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Icon } from "@iconify/react";
import { useMediaQuery, useTheme } from "@mui/system";
import { useMutation, useQueryClient } from "react-query";
import { IAdminMsgView } from "../../dto/CreateAdminMessageViewDto";
import { User } from "../../api/user";
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import { TUser } from "../../shared/types/user.type";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);

type MessageBannerProps = {
  message: AdminMessage;
};

function MessageBanner({ message }: MessageBannerProps) {
  //Get the user lang to match lang to match message with it lang
  const { i18n } = useTranslation();
  const queryCLient = useQueryClient();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm")); // Check if screen is "sm" or smaller
  const removeMessageMutation = useMutation((data: IAdminMsgView) =>
    User.removeUserMessage(data),
  );
  const user = useSelector<RootState>(
    (state) => state.user.user,
  ) as TUser | null;

  const currentLang = i18n.language;

  const messageToDisplay = useMemo(() => {
    try {
      let messages = JSON.parse(message.message);

      let messageByLang = messages?.[currentLang] ?? messages?.["en"];

      return messageByLang;
    } catch (error) {
      console.log({ error });
    }
  }, [message.id, currentLang]);

  const removeMessage = async () => {
    if (!user) return;
    try {
      await removeMessageMutation.mutateAsync({
        admin_message: message["@id"],
        date: dayjs().utc(true).toISOString(),
        source: "web",
        user: user["@id"],
      });
      queryCLient.refetchQueries({ queryKey: ["admin_messages"] });
    } catch (error) {}
  };

  const displayOnMobile = message?.display_on_mobile_app;

  if (!message || (isSmallScreen && !displayOnMobile)) return null;

  return (
    <Banner bgColor={message?.background_color ?? "#ffa200"}>
      {!isSmallScreen && (
        <Icon
          width={22}
          height={22}
          style={{ width: 22, height: 22, flexShrink: 0 }}
          icon="basil:notification-on-outline"
        ></Icon>
      )}
      <Typography variant="subtitle2" flex={1}>
        <span dangerouslySetInnerHTML={{ __html: messageToDisplay }}></span>
      </Typography>
      {message.requires_action && (
        <IconButton
          disabled={removeMessageMutation.isLoading}
          size="small"
          sx={{ alignSelf: "start" }}
          onClick={removeMessage}
        >
          <Icon color="white" icon={"material-symbols:close-rounded"} />
        </IconButton>
      )}
    </Banner>
  );
}

type StyledBannerProps = {
  bgColor: string;
};

export const Banner = styled.div<StyledBannerProps>`
  width: 100%;
  padding: 12px 15px;
  display: flex;
  align-items: center;
  gap: 10px;
  background-color: ${(props) => props.bgColor};
  font-weight: 400;
`;

export default MessageBanner;
